import { QueryResult } from '@apollo/client/react';
import { get } from 'lodash';
import moment from 'moment';
import { Fragment, PropsWithChildren, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { PAGE_CONTENT_WIDTH, tokens } from '../theme/tokens';
import { Card } from './Card';
import { Container } from './Container';
import { Empty } from './Empty';
import { FlexGap } from './FlexGap';
import { InfiniteScroll } from './InfiniteScroll';
import { Skeleton } from './Skeleton';
import { Switch } from './Switch';
import { Heading3, Paragraph } from './Text';

function itemsFromPagination<T>(array?: { edges: Array<{ node: T }> }) {
	if (!array) {
		return undefined;
	}

	return array.edges
		.slice()
		.reverse()
		.map(({ node }) => node as T);
}

function DynamicContainer(props: PropsWithChildren<{ compact?: boolean }>) {
	if (props.compact) {
		return <>{props.children}</>;
	}
	return (
		<Container mt={`${tokens.gap.page}px`} mb={4} wide={PAGE_CONTENT_WIDTH}>
			{props.children}
		</Container>
	);
}

const EmptyState = styled.div<{ $compact?: boolean }>`
	display: flex;
	justify-content: center;
	padding-top: ${(props) => (props.$compact ? 0 : tokens.gap.page)}px;

	> * {
		max-width: none;
	}
`;

const ArchiveToggle = styled.div`
	margin-bottom: ${tokens.gap.page}px;
`;

const GroupTitle = styled.a`
	display: inline-flex;
	text-decoration: none;
`;

export function CardList<Q extends object, T extends { id: string }, GroupKey = unknown>(props: {
	translationName: string;
	query: QueryResult<Q>;
	toolbar?: ReactNode;
	path: string;
	archivedQuery?: QueryResult<Q>;
	fetchArchived?: () => void;
	groupedByYear?: boolean;
	skeletonRepeat?: number;
	disablePagination?: boolean;
	compact?: boolean;
	/** Wrap rows in a Card. Defaults to true. */
	framed?: boolean;

	icon: ReactNode;
	renderSkeleton: () => ReactNode;
	renderCard: (item: T) => ReactNode;

	groupOptions?: GroupOptions<T, GroupKey>;
	sortItems?: (a: T, b: T) => number;
}) {
	const { t } = useTranslation();

	const [showArchived, setShowArchived] = useState<boolean>(false);

	const rawItems = get(props.query.data, props.path);

	const items: T[] | undefined = useMemo(() => {
		const items = props.disablePagination ? rawItems : itemsFromPagination<T>(rawItems);
		return props.sortItems ? items?.sort(props.sortItems) : items;
	}, [props.disablePagination, props.sortItems, rawItems]);

	const archivedItems = useMemo(() => {
		const items = props.archivedQuery?.data
			? itemsFromPagination<T>(get(props.archivedQuery.data, props.path))
			: null;
		return props.sortItems ? items?.sort(props.sortItems) : items;
	}, [props.archivedQuery, props.sortItems, props.path]);

	const groupOptions = useMemo(() => {
		if (props.groupedByYear) {
			return {
				groupBy: (item: T & { createdAt: moment.MomentInput }) => moment(item.createdAt).year(),
				groupSort: (a, b) => b - a,
				groupTitle: (key) => moment().year(key).format('YYYY'),
			} as GroupOptions<T, GroupKey & number>;
		}

		return props.groupOptions;
	}, [props.groupedByYear, props.groupOptions]);
	const { itemsByGroup, groups } = useGroupedItems(items, groupOptions);

	useEffect(() => {
		if (!showArchived) {
			return;
		}

		props.fetchArchived?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showArchived]);

	const framed = props.framed ?? true;
	const skeletonCount = props.skeletonRepeat ?? 10;

	const empty = (title: string, description: string) => (
		<EmptyState $compact={props.compact}>
			<Empty icon={props.icon} title={title} description={description} />
		</EmptyState>
	);

	const loading = maybeFrame(
		framed,
		<Skeleton.Repeat count={skeletonCount}>{props.renderSkeleton()}</Skeleton.Repeat>,
	);

	const cards = (nodes: T[]) =>
		nodes.map((item) => <Fragment key={item.id}>{props.renderCard(item)}</Fragment>);

	const infinite = (query: QueryResult<Q>) => (
		<InfiniteScroll<Q> query={query} path={props.path} reverse>
			<Skeleton.Repeat count={5}>{props.renderSkeleton()}</Skeleton.Repeat>
		</InfiniteScroll>
	);

	const list = (nodes: T[], query: QueryResult<Q>) => {
		if (groups && !showArchived) {
			return (
				<FlexGap flexDirection="column" gap={tokens.gap.section}>
					{groups.map(({ key, title }) => (
						<FlexGap key={title} id={title} flexDirection="column" gap={tokens.gap.list}>
							<GroupTitle href={`#${title}`}>
								<Heading3>{title}</Heading3>
							</GroupTitle>
							{maybeFrame(framed, cards(itemsByGroup.get(key) ?? []))}
						</FlexGap>
					))}
					{infinite(query)}
				</FlexGap>
			);
		}

		return maybeFrame(
			framed,
			<>
				{cards(nodes)}
				{infinite(query)}
			</>,
		);
	};

	const contents = (
		nodes: T[] | null | undefined,
		query: QueryResult<Q>,
		kind: 'empty' | 'archived.empty',
	) => {
		if (!nodes) {
			return loading;
		}

		if (nodes.length === 0) {
			return empty(
				t(`${props.translationName}.list.${kind}.title`),
				t(`${props.translationName}.list.${kind}.description`),
			);
		}

		return list(nodes, query);
	};

	return (
		<DynamicContainer compact={props.compact}>
			{props.archivedQuery && (
				<ArchiveToggle>
					<Switch
						label={t(`${props.translationName}.list.archived.button`)}
						on={showArchived}
						onChange={(on) => setShowArchived(on)}
					/>
				</ArchiveToggle>
			)}

			{props.toolbar}

			{!showArchived ? (
				contents(items, props.query, 'empty')
			) : (
				<FlexGap flexDirection="column" gap={tokens.gap.list}>
					<Paragraph>{t(`${props.translationName}.list.archived.title`)}</Paragraph>
					{contents(archivedItems, props.archivedQuery!, 'archived.empty')}
				</FlexGap>
			)}
		</DynamicContainer>
	);
}

type GroupOptions<T, GroupKey> = {
	/**
	 * Maps an item to a group key, i.e. in what group the item should be displayed. Grouping is optional.
	 */
	groupBy: (item: T) => GroupKey;

	/**
	 * Specifies the compare function for sorting the group keys. If omitted, defaults to ascending order.
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort|Array.prototype.sort()}
	 */
	groupSort?: (a: GroupKey, b: GroupKey) => number;

	/**
	 * The title to display above the group of items for a given group key.
	 */
	groupTitle?: (key: GroupKey) => string;
};

function useGroupedItems<T extends { id: string }, GroupKey>(
	items: T[] | undefined,
	groupOptions: GroupOptions<T, GroupKey> | undefined,
) {
	const itemsByGroup = useMemo(() => {
		if (!groupOptions || !items) {
			return new Map<GroupKey, T[]>();
		}

		const grouped = new Map<GroupKey, T[]>();

		items.forEach((item) => {
			const key = groupOptions.groupBy(item);
			grouped.set(key, [...(grouped.get(key) ?? []), item]);
		});

		return grouped;
	}, [items, groupOptions]);

	const groups = useMemo(() => {
		if (!groupOptions) {
			return undefined;
		}

		const groupKeys = Array.from(itemsByGroup.keys());

		return groupKeys.sort(groupOptions.groupSort).map((key) => ({
			key,
			title: groupOptions.groupTitle?.(key) ?? String(key),
		}));
	}, [itemsByGroup, groupOptions]);

	return { itemsByGroup, groups };
}

function maybeFrame(framed: boolean, children: ReactNode) {
	if (!framed) {
		return children;
	}
	return <Card p={0}>{children}</Card>;
}

export type { GroupOptions as CardListGroupOptions };
